import getConfig from 'next/config'
import { useEffect, useState, useContext } from 'react'

import { PATHS, L_STORAGE_KEYS } from '@src/consts'
import {
  useFetchUserMe,
  useCustomRouter,
  useLoginWithEmailAndPassword,
} from '@src/hooks'
import { TfaTokenContext } from '@src/pages/_app.page'
import { getUser, lStorage, toAuthenticationType } from '@src/utils'

import { useMoveToNextUrl } from '../use-move-to-next-url'
import { STAGING_API_ENDPOINT } from './const'
import { UseSignin } from './type'

export const useSignin = ({
  setLoginErrors,
  getSigninFormValue,
  handleChangePageState,
  onLoginWithEmailAndPasswordError,
}: UseSignin) => {
  const { router, isString } = useCustomRouter()
  const query = router.query
  const loginChallenge = isString(query.login_challenge)
    ? query.login_challenge
    : ''
  const { publicRuntimeConfig } = getConfig()
  const { API_ENDPOINT } = publicRuntimeConfig
  const tfaTokenContext = useContext(TfaTokenContext)

  // To be able to request the Staging API from Local to increase development efficiency
  const isConnectedLocalToStg =
    API_ENDPOINT === STAGING_API_ENDPOINT &&
    process.env.NODE_ENV !== 'production'

  const [enabledFetchUserMe, setEnabledFetchUserMe] = useState(false)

  const fetchUserMe = useFetchUserMe({ enabled: enabledFetchUserMe })

  const loginWithEmailAndPasswordMutation = useLoginWithEmailAndPassword()
  const isLoadingLoginWithEmailAndPassword =
    loginWithEmailAndPasswordMutation.isPending
  const { moveToNextUrl } = useMoveToNextUrl()

  function handleLoginWithEmailAndPassword(turnstileToken: string | null) {
    loginWithEmailAndPasswordMutation.mutate(
      {
        loginWithPasswordForm: {
          email: getSigninFormValue().email,
          password: getSigninFormValue().password,
          // Bot protection (CRD-4626)
          turnstileToken: turnstileToken ?? undefined,
        },
      },
      {
        onSuccess: async ({ data }) => {
          // To be able to request the Staging API from Local to increase development efficiency
          if (isConnectedLocalToStg && data.token) {
            lStorage.setItem({
              value: data.token,
              key: L_STORAGE_KEYS.TOKEN,
            })
          }
          if (data.tfaToken && data.tfaTokenExpiresAt) {
            tfaTokenContext?.setTfaToken(data.tfaToken)
            tfaTokenContext?.setTfaTokenExpiresAt(data.tfaTokenExpiresAt)
          }
          if (!data.twoFactorAuthRegisterStatus) {
            setEnabledFetchUserMe(true)
          } else if (data.twoFactorAuthRegisterStatus === 'APP') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            router.push({
              pathname: PATHS.SIGNIN_TFA_APP,
              query,
            })
          } else if (data.twoFactorAuthRegisterStatus === 'SMS') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.SMS_PHONE_NUMBER,
              value: data.smsPhoneNumber,
            })
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            router.push({
              pathname: PATHS.SIGNIN_TFA_SMS,
              query,
            })
          } else if (data.twoFactorAuthRegisterStatus === 'EMAIL') {
            lStorage.setItem({
              key: L_STORAGE_KEYS.MAIL_ADDRESS,
              value: data.email,
            })
            handleChangePageState('EMAIL_MFA')
          }
        },
        onError: (error) => {
          if (error.data.errors.errors) {
            setLoginErrors(error.data.errors.errors)
          } else if (error.data.errors.password) {
            setLoginErrors(error.data.errors.password)
          } else if (error.data.errors.turnstileToken) {
            // Turnstile field errors (missing/malformed token) only happen on
            // tampered or bot requests, so the BE field-level text would be
            // meaningless to real users — show a generic message instead
            setLoginErrors(['リクエストが正しくありません'])
          } else {
            setLoginErrors(['不明なエラーが発生しました'])
          }

          // Turnstile tokens are single-use; reset the widget on every error
          // path so the user can re-challenge (covers network errors too).
          onLoginWithEmailAndPasswordError?.()
        },
      }
    )
  }

  useEffect(() => {
    if (fetchUserMe.isSuccess) {
      const rawUserMe = fetchUserMe.data.data
      const convertedResponse = toAuthenticationType(rawUserMe)
      lStorage.setItem({ key: 'user', value: convertedResponse })
      moveToNextUrl()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUserMe.isSuccess])

  useEffect(() => {
    const user = getUser()
    // user logged in but not accepted oauth yet
    if (user?.id && !!loginChallenge) {
      router.push({
        pathname: PATHS.OAUTH_AUTHORIZE_ACCEPT,
        query: {
          login_challenge: loginChallenge,
          subject: user.id,
        },
      })
    }
  }, [loginChallenge, router])

  return {
    handleLoginWithEmailAndPassword,
    isLoadingLoginWithEmailAndPassword,
  }
}
